import { useCallback, useEffect, useRef, useState } from "react";
import { classifyMediaError, type MediaErrorCause } from "../../call/lib/deviceErrors";

export type RecorderStatus = "idle" | "requesting" | "recording";

export interface VoiceRecorder {
  status: RecorderStatus;
  error: MediaErrorCause | null;
  durationMs: number;
  start: () => Promise<void>;
  stop: () => Promise<Blob | null>;
  cancel: () => void;
}

/** Pick the first MediaRecorder mime type the browser actually supports. */
function pickMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  for (const c of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch {
      /* isTypeSupported can throw on some engines — keep trying */
    }
  }
  return ""; // let the browser choose its default
}

export function useVoiceRecorder(): VoiceRecorder {
  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [error, setError] = useState<MediaErrorCause | null>(null);
  const [durationMs, setDurationMs] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const statusRef = useRef<RecorderStatus>("idle");

  const setStatusBoth = useCallback((next: RecorderStatus) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
  }, []);

  const start = useCallback(async () => {
    if (statusRef.current !== "idle") return;
    setError(null);
    setDurationMs(0);
    setStatusBoth("requesting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorderRef.current = recorder;
      recorder.start();
      const startedAt = Date.now();
      timerRef.current = setInterval(() => setDurationMs(Date.now() - startedAt), 200);
      setStatusBoth("recording");
    } catch (err) {
      cleanup();
      setError(classifyMediaError(err));
      setStatusBoth("idle");
    }
  }, [cleanup, setStatusBoth]);

  const stop = useCallback(async (): Promise<Blob | null> => {
    const recorder = recorderRef.current;
    if (!recorder || statusRef.current !== "recording") {
      cleanup();
      setStatusBoth("idle");
      return null;
    }
    const mimeType = recorder.mimeType || "audio/webm";
    const blob = await new Promise<Blob>((resolve) => {
      recorder.onstop = () => {
        const parts = chunksRef.current;
        resolve(new Blob(parts, { type: mimeType }));
      };
      recorder.stop();
    });
    cleanup();
    setStatusBoth("idle");
    return blob.size > 0 ? blob : null;
  }, [cleanup, setStatusBoth]);

  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.onstop = null;
      try {
        recorder.stop();
      } catch {
        /* already stopped */
      }
    }
    cleanup();
    setDurationMs(0);
    setStatusBoth("idle");
  }, [cleanup, setStatusBoth]);

  // Release the mic if the component unmounts mid-recording.
  useEffect(() => cleanup, [cleanup]);

  return { status, error, durationMs, start, stop, cancel };
}
